import * as THREE from 'three';
import { isFeatureResultMesh } from '../../../mesh/feature/FeatureResultMesh';
import type { ExtrudeFeatureEditDefinition } from './ExtrudeFeatureEditDefinition';
import { CutExtrudeDefinitionCoordinator, type CutExtrudePreviewSeed } from './CutExtrudeDefinitionCoordinator';
import type { ExtrudeSessionTarget } from './ExtrudeFeatureSessionBridge';
import { ExtrudeHostedTargetBodyResolver } from './ExtrudeHostedTargetBodyResolver';
import { BossExtrudeSceneResolver } from './BossExtrudeSceneResolver';
import { resolveModelTopologyOwnerResultMesh } from '../../../utils/feature/resolveModelTopologyOwnerResultMesh';
import { convertMmToM } from '../../../utils/unit';
/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/extrude 层；
 * 2. 负责解析 cut-extrude 在创建态进入时的默认预览种子；
 * 3. 创建态与后续同类命令应共同复用这条默认值策略，不允许 web-client 写死常量。
 *
 * 文件职责：
 * 1. 解析当前切除命中的目标体；
 * 2. 计算目标体沿切除轴的厚度，并追加 SolidWorks 对齐要求中的 +1 种子偏移；
 * 3. 在宿主目标体缺失时，提供单结果体场景的安全回退。
 */

const CUT_EXTRUDE_PREVIEW_EXTRA_DEPTH = convertMmToM(1);
const MIN_CUT_EXTRUDE_DEPTH = 1e-6;
const worldPoint = new THREE.Vector3();

/**
 * 拉伸切除默认预览种子解析器。
 */
export class CutExtrudePreviewSeedResolver {
    /** 场景轮廓解析器。 */
    private readonly sceneResolver = new BossExtrudeSceneResolver();
    /** 宿主支持体解析器（仅用于预览提示，不锁定切除目标）。 */
    private readonly hostedTargetBodyResolver = new ExtrudeHostedTargetBodyResolver();
    /**
     * 解析默认预览种子。
     *
     * @param input 解析输入。
     * @returns 预览种子。
     */
    public resolve(input: {
        scene: THREE.Scene | null;
        target: ExtrudeSessionTarget;
        definition: ExtrudeFeatureEditDefinition;
    }): CutExtrudePreviewSeed {
        const synchronizedDefinition = CutExtrudeDefinitionCoordinator.synchronizeDefinition(input.definition);
        const hostedTarget = this.hostedTargetBodyResolver.resolve({
            scene: input.scene,
            target: input.target,
            definition: synchronizedDefinition,
        });
        const targetBodyId = hostedTarget.targetBodyId;
        const axis = input.target.normal.clone().normalize();
        const previewDepth = this.resolvePreviewDepth(input.scene, targetBodyId, axis);
        const region = input.target.regions?.[0];

        return {
            direction1Depth: previewDepth !== null
                ? Math.max(MIN_CUT_EXTRUDE_DEPTH, previewDepth + CUT_EXTRUDE_PREVIEW_EXTRA_DEPTH)
                : Math.max(MIN_CUT_EXTRUDE_DEPTH, synchronizedDefinition.direction1.depth),
            flipSideToCut: synchronizedDefinition.cutOptions?.flipSideToCut ?? false,
            targetBodyId,
            targetBodyThickness: previewDepth,
            hostedContourInput: region
                ? {
                    sketchId: region.sketchId,
                    contourId: region.regionId,
                    targetBodyId,
                    supportFaceObjectId: hostedTarget.supportFaceObjectId,
                    supportFaceFaceIndex: hostedTarget.supportFaceFaceIndex,
                }
                : undefined,
        };
    }

    /**
     * 解析预览深度——沿切除轴取所有可切除结果体的最大厚度。     *
     * 设计说明：
     * 1. 切除深度应覆盖刀具体可能穿过的所有实体，而非仅草图宿主实体；
     * 2. 单实体场景与多实体场景统一走候选体最大厚度策略；
     * 3. 无法收集候选体时返回 null，由上层回退到定义中的默认深度。
     *
     * @param scene 当前场景。
     * @param targetBodyId 预览提示用宿主体 ID（导入体拓扑回退）。
     * @param axis 切除轴向。
     * @returns 沿轴向的最大厚度；无法计算时返回 null。
     */
    private resolvePreviewDepth(
        scene: THREE.Scene | null,
        targetBodyId: string | null,
        axis: THREE.Vector3,
    ): number | null {
        if (!scene) {
            return null;
        }

        const candidates = this.collectResultBodyCandidates(scene);
        if (candidates.length === 0 && targetBodyId) {
            const fallbackTarget = this.resolvePreviewTargetObject(scene, targetBodyId);
            if (fallbackTarget) {
                return this.computeObjectExtentAlongAxis(fallbackTarget, axis);
            }
            return null;
        }

        if (candidates.length === 0) {
            return null;
        }

        let maxThickness = 0;
        let hasAny = false;
        for (const body of candidates) {
            const thickness = this.computeObjectExtentAlongAxis(body, axis);
            if (thickness !== null) {
                maxThickness = Math.max(maxThickness, thickness);
                hasAny = true;
            }
        }
        return hasAny ? maxThickness : null;
    }

    /**
     * 解析导入体拓扑或显式结果体对象（仅预览深度回退）。
     *
     * @param scene 当前场景。
     * @param targetBodyId 目标体 ID。
     * @returns 场景对象。
     */
    private resolvePreviewTargetObject(
        scene: THREE.Scene,
        targetBodyId: string | null,
    ): THREE.Object3D | null {
        if (!targetBodyId) {
            return null;
        }

        const topologyTarget = resolveModelTopologyOwnerResultMesh({
            scene,
            ownerObjectId: targetBodyId,
        });
        if (topologyTarget) {
            return topologyTarget;
        }

        return this.sceneResolver.findSceneObjectById(scene, targetBodyId);
    }

    /**
     * 收集场景中的正式结果体候选。
     *
     * @param scene 当前场景。
     * @returns 结果体对象列表。
     */
    private collectResultBodyCandidates(scene: THREE.Scene): THREE.Object3D[] {
        const candidates: THREE.Object3D[] = [];
        scene.traverse((object) => {
            if (isFeatureResultMesh(object)) {
                candidates.push(object);
            }
        });
        return candidates;
    }

    /**
     * 计算对象沿指定轴向的厚度。
     *
     * @param object 目标对象。
     * @param axis 轴向。
     * @returns 厚度；无法解析时返回 null。
     */
    private computeObjectExtentAlongAxis(
        object: THREE.Object3D,
        axis: THREE.Vector3,
    ): number | null {
        const normalizedAxis = axis.clone().normalize();
        if (normalizedAxis.lengthSq() <= 1e-12) {
            return null;
        }

        object.updateWorldMatrix(true, true);

        let minProjection = Number.POSITIVE_INFINITY;
        let maxProjection = Number.NEGATIVE_INFINITY;
        let hasVertex = false;

        object.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) {
                return;
            }
            const geometry = child.geometry;
            if (!(geometry instanceof THREE.BufferGeometry)) {
                return;
            }
            const positionAttribute = geometry.getAttribute('position');
            if (!(positionAttribute instanceof THREE.BufferAttribute)) {
                return;
            }

            for (let index = 0; index < positionAttribute.count; index += 1) {
                worldPoint.fromBufferAttribute(positionAttribute, index);
                child.localToWorld(worldPoint);
                const projection = worldPoint.dot(normalizedAxis);
                minProjection = Math.min(minProjection, projection);
                maxProjection = Math.max(maxProjection, projection);
                hasVertex = true;
            }
        });

        if (!hasVertex) {
            return null;
        }
        return Math.max(0, maxProjection - minProjection);
    }
}
